import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const HEADER_TONES = {
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
  sky:     "bg-sky-50 text-sky-800 border-sky-100",
  amber:   "bg-amber-50 text-amber-800 border-amber-100",
  rose:    "bg-rose-50 text-rose-800 border-rose-100",
  violet:  "bg-violet-50 text-violet-800 border-violet-100",
};

export function PageHeader({ title, subtitle, actions, tone, eyebrow, eyebrowIcon: EyebrowIcon, testid = "page-header", showBack = true }) {
  const nav = useNavigate();
  const loc = useLocation();
  const isDashboard = loc.pathname === "/dashboard";
  const canGoBack = showBack && !isDashboard;
  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      nav(-1);
    } else {
      nav("/dashboard");
    }
  };

  // Modern dark-gradient hero — matches Leasing Assistant / Marketing Hub /
  // RUBS / Eviction / Key Wall so the whole app reads as one product.
  if (tone === "modern-dark") {
    return (
      <div data-testid={testid}
        className="mb-6 -mx-4 sm:-mx-6 rounded-b-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-white overflow-hidden">
        <div className="px-5 sm:px-8 pt-8 pb-9 max-w-6xl mx-auto">
          {eyebrow && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-300">
              {EyebrowIcon && <EyebrowIcon className="w-3.5 h-3.5"/>} {eyebrow}
            </div>
          )}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">{title}</h1>
              {subtitle && <p className="mt-3 text-base text-slate-300 leading-relaxed max-w-2xl">{subtitle}</p>}
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
          </div>
        </div>
      </div>
    );
  }

  // Pill-Pack-style clean card variant (pastel top strip + white body)
  // is opted into per-page by passing `tone`. Legacy pages without a
  // tone keep the original two-column layout.
  if (tone && HEADER_TONES[tone]) {
    return (
      <div
        data-testid={testid}
        className="mb-6 rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden"
      >
        <div className={`px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] border-b ${HEADER_TONES[tone]}`}>
          {canGoBack ? (
            <button
              onClick={handleBack}
              data-testid="page-back-button"
              className="inline-flex items-center gap-1.5 hover:opacity-80"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          ) : (
            <span>{subtitle || title}</span>
          )}
        </div>
        <div className="px-5 md:px-7 py-5 md:py-7 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-slate-950">{title}</h1>
            {subtitle && canGoBack && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testid} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
      <div className="min-w-0">
        {canGoBack && (
          <button
            onClick={handleBack}
            data-testid="page-back-button"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-green-800 mb-3 transition-colors -ml-1 px-1 py-0.5 rounded"
          >
            <ArrowLeft className="w-4 h-4"/> Back
          </button>
        )}
        <h1 className="font-display text-2xl md:text-[28px] font-bold tracking-tight text-slate-950">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className = "", ...props }) {
  return (
    <div
      className={`bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function Stat({ label, value, sub, icon: Icon, accent = "text-slate-900", testid }) {
  return (
    <Card className="p-5 card-lift" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
          <div className={`mt-2 font-display text-3xl font-bold ${accent}`}>{value}</div>
          {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
        </div>
        {Icon && (
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-800 flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </Card>
  );
}

export function Pill({ children, tone = "slate", className = "" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-green-100 text-green-800",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-800",
    violet: "bg-violet-100 text-violet-800",
  };
  return <span className={`pill ${tones[tone] || tones.slate} ${className}`}>{children}</span>;
}

export function Button({ children, variant = "primary", className = "", as: As = "button", ...props }) {
  const base = "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-green-800 text-white hover:bg-green-900",
    secondary: "bg-white text-slate-900 border border-slate-200 hover:bg-slate-50",
    ghost: "text-slate-700 hover:bg-slate-100",
    danger: "bg-red-600 text-white hover:bg-red-700",
    outline: "bg-transparent text-green-800 border border-green-800 hover:bg-green-50",
  };
  return (
    <As className={`${base} ${variants[variant] || variants.primary} ${className}`} {...props}>
      {children}
    </As>
  );
}

export function Input({ label, error, className = "", testid, required, ...props }) {
  return (
    <label className="block">
      {label && (
        <div className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
          <span>{label}</span>
          {required && (
            <span className="text-[10px] uppercase tracking-wider font-bold text-rose-600" aria-hidden="true">
              required
            </span>
          )}
        </div>
      )}
      <input
        data-testid={testid}
        required={required}
        aria-required={required ? "true" : undefined}
        className={`w-full bg-white border border-slate-300 text-slate-900 rounded-md px-3.5 py-2.5 text-sm focus:border-green-800 focus:ring-2 focus:ring-green-800/15 outline-none transition-all placeholder:text-slate-400 ${error ? "border-red-400 focus:border-red-500 focus:ring-red-500/15" : ""} ${className}`}
        {...props}
      />
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </label>
  );
}

export function Textarea({ label, error, className = "", testid, ...props }) {
  return (
    <label className="block">
      {label && <div className="text-sm font-medium text-slate-700 mb-1.5">{label}</div>}
      <textarea
        data-testid={testid}
        className={`w-full bg-white border border-slate-300 text-slate-900 rounded-md px-3.5 py-2.5 text-sm focus:border-green-800 focus:ring-2 focus:ring-green-800/15 outline-none transition-all min-h-[100px] placeholder:text-slate-400 ${className}`}
        {...props}
      />
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </label>
  );
}

export function Select({ label, children, className = "", testid, ...props }) {
  return (
    <label className="block">
      {label && <div className="text-sm font-medium text-slate-700 mb-1.5">{label}</div>}
      <select
        data-testid={testid}
        className={`w-full bg-white border border-slate-300 text-slate-900 rounded-md px-3 py-2.5 text-sm focus:border-green-800 focus:ring-2 focus:ring-green-800/15 outline-none transition-all ${className}`}
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function EmptyState({ title, description, action, icon: Icon, testid = "empty-state" }) {
  return (
    <Card className="py-16 px-6 text-center" data-testid={testid}>
      {Icon && (
        <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6"/>
        </div>
      )}
      <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
      {description && <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </Card>
  );
}

/**
 * Loader — inline spinner with a stuck-state guard. If the spinner is
 * still visible after 8 seconds, we surface a "Still loading… reload
 * the page" fallback UI so tenants/landlords never stare at an
 * infinite spinner (a common complaint when the /auth/me network
 * request stalls on flaky connections).
 */
export function Loader({ label = "Loading…", testid = "loader", timeoutMs = 8000 }) {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStuck(true), timeoutMs);
    return () => clearTimeout(t);
  }, [timeoutMs]);
  if (stuck) {
    return (
      <div className="flex items-center justify-center py-16 px-6" data-testid={`${testid}-stuck`}>
        <div className="text-center max-w-sm">
          <div className="text-sm font-semibold text-slate-900">Still loading…</div>
          <div className="text-xs text-slate-500 mt-1">
            This is taking longer than expected. Your network may have hiccupped.
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            data-testid={`${testid}-reload`}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800"
          >
            Reload the page
          </button>
          <div className="mt-2">
            <a href="/" className="text-xs text-slate-500 underline hover:text-slate-700">Back to home</a>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center py-16" data-testid={testid}>
      <div className="flex items-center gap-3 text-slate-500 text-sm">
        <div className="w-4 h-4 border-2 border-slate-300 border-t-green-800 rounded-full animate-spin"></div>
        {label}
      </div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, testid = "modal" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-200 max-h-[92vh] overflow-y-auto animate-fade-in-up">
        <div className="px-6 pt-5 pb-3 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
